import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

const ARCHIVED_ITEMS = [
  {
    to: '/archived/team-bonus',
    icon: '🌳',
    en: 'Team Bonus (Old)',
    zh: '团队奖金（旧版）',
    descEn: 'The original weak-leg binary-matching Team Bonus system. Superseded by the new ranking-based Team Bonus at /team-bonus, kept here unchanged for reference.',
    descZh: '原有的弱腿对碰团队奖金系统。已被 /team-bonus 的新版星级团队奖金取代，此处保留原始内容供参考。',
  },
  {
    to: '/archived/direct-referral',
    icon: '💰',
    en: 'Direct Referral',
    zh: '直推',
    descEn: 'The original standalone Direct Referral admin page. Superseded by the consolidated /member-rewards view; kept here unchanged for reference and as a fallback.',
    descZh: '原有的独立直推管理页面。已被整合后的 /member-rewards 取代，此处保留原始内容供参考及备用。',
  },
  {
    to: '/archived/passive',
    icon: '📅',
    en: 'Passive & Team Booster',
    zh: '被动与团队激励',
    descEn: 'The original standalone Passive and Team Booster admin page. Superseded by the consolidated /member-rewards view; kept here unchanged for reference and as a fallback.',
    descZh: '原有的独立被动与团队激励管理页面。已被整合后的 /member-rewards 取代，此处保留原始内容供参考及备用。',
  },
  {
    to: '/archived/signup',
    icon: '📝',
    en: 'Signup (Old)',
    zh: '注册（旧版）',
    descEn: 'The original public self-service signup form (account only, no package purchase). Superseded by the Signup Wizard at /signup, which also offers immediate card payment for a package; kept here unchanged for reference.',
    descZh: '原有的公开自助注册表单（仅创建账户，不含套餐购买）。已被 /signup 的注册向导取代（该向导还提供即时银行卡付款购买套餐功能），此处保留原始内容供参考。',
  },
  {
    to: '/archived/signup-internal',
    icon: '🧑‍💼',
    en: 'Internal Signup (Old)',
    zh: '内部注册（旧版）',
    descEn: 'The original standalone internal/sales-team signup form. Superseded by the Signup Wizard at /signup-internal, which adds a card-payment-link funding option alongside the existing Credit/RnR funding; kept here unchanged for reference.',
    descZh: '原有的独立内部/销售团队注册表单。已被 /signup-internal 的注册向导取代（在现有认购积分/RnR 资助方式之外新增银行卡付款链接选项），此处保留原始内容供参考。',
  },
  // Not superseded -- still fully live pages, just moved off WELLIS-BJOECHIA's
  // own main nav (desk cleanup, explicit request) while staying unchanged
  // and reachable here for anyone with /archived access.
  {
    to: '/members',
    icon: '👥',
    en: 'Members',
    zh: '会员',
    descEn: 'Still a live, active page -- just removed from the main nav to declutter.',
    descZh: '仍为正常使用中的页面——仅从主导航中移除以精简界面。',
  },
  {
    to: '/packages',
    icon: '📦',
    en: 'Packages',
    zh: '套餐',
    descEn: 'Still a live, active page -- just removed from the main nav to declutter.',
    descZh: '仍为正常使用中的页面——仅从主导航中移除以精简界面。',
  },
  {
    to: '/member-rewards',
    icon: '🎁',
    en: 'Member Rewards',
    zh: '会员奖励',
    descEn: 'Still a live, active page -- just removed from the main nav to declutter.',
    descZh: '仍为正常使用中的页面——仅从主导航中移除以精简界面。',
  },
  {
    to: '/signup-wizard',
    icon: '🧙',
    en: 'Signup Wizard',
    zh: '注册向导',
    descEn: 'Still a live, active page -- just removed from the main nav to declutter.',
    descZh: '仍为正常使用中的页面——仅从主导航中移除以精简界面。',
  },
  {
    to: '/signup-internal',
    icon: '📝',
    en: 'Internal Signup',
    zh: '内部注册',
    descEn: 'Still a live, active page -- just removed from the main nav to declutter.',
    descZh: '仍为正常使用中的页面——仅从主导航中移除以精简界面。',
  },
];

export default function Archived() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const tt = (en, zh) => (isZh ? zh : en);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'superadmin') {
      navigate('/dashboard');
    }
  }, [authLoading, user, navigate]);

  if (!user || user.role !== 'superadmin') {
    return null;
  }

  return (
    <div className="min-h-screen px-4 pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-headline-lg font-bold" style={{ color: '#c9a961' }}>
            {tt('Archived', '已归档')}
          </h1>
          <p className="text-body-sm mt-2" style={{ color: '#b8a68f' }}>
            {tt(
              'Superseded features kept for reference. Not linked from the main navigation.',
              '已被取代的功能，保留供参考，不在主导航中显示。'
            )}
          </p>
        </div>

        <div className="space-y-3">
          {ARCHIVED_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="block border rounded-lg p-4 hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}
            >
              <p className="text-body-md font-bold" style={{ color: '#c9a961' }}>
                {item.icon} {tt(item.en, item.zh)}
              </p>
              <p className="text-body-sm mt-1" style={{ color: '#b8a68f' }}>
                {tt(item.descEn, item.descZh)}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
